import { create } from 'zustand';
import type { Agent, Workspace, Widget } from '../types';
import type { AuthUser } from '../api/auth.api';
import type { TrialStatus } from '../api/trial.api';

type AgentsByWorkspace = Record<string, Agent[]>;
type WidgetsByAgent = Record<string, Widget | undefined>;

interface AppState {
  // Auth
  user: AuthUser | null;
  isAuthLoading: boolean;

  // Workspaces
  workspaces: Workspace[];
  activeWorkspaceId: string | null;

  // Agents / Knowledge / Widgets (global caches)
  agentsByWorkspace: AgentsByWorkspace;
  widgetsByAgent: WidgetsByAgent;

  // Trial (CUSTOMER only — see useTrialStatus). fetchedAt is ms since epoch, 0
  // when never fetched.
  trialStatus: TrialStatus | null;
  trialStatusFetchedAt: number;

  // Mutators
  setUser: (user: AuthUser | null) => void;
  setAuthLoading: (loading: boolean) => void;

  setWorkspaces: (workspaces: Workspace[]) => void;
  setActiveWorkspaceId: (workspaceId: string | null) => void;

  setAgentsForWorkspace: (workspaceId: string, agents: Agent[]) => void;
  setWidgetForAgent: (agentId: string, widget: Widget | undefined) => void;
  setTrialStatus: (status: TrialStatus | null) => void;

  reset: () => void;
}

export const useAppStore = create<AppState>((set) => ({
  user: null,
  // Start in loading state so protected routes don't redirect
  // before we have a chance to refresh the session.
  isAuthLoading: true,

  workspaces: [],
  activeWorkspaceId: null,

  agentsByWorkspace: {},
  widgetsByAgent: {},

  trialStatus: null,
  trialStatusFetchedAt: 0,

  setUser: (user) => set({ user }),
  setAuthLoading: (isAuthLoading) => set({ isAuthLoading }),

  setWorkspaces: (workspaces) => set({ workspaces }),
  setActiveWorkspaceId: (activeWorkspaceId) => set({ activeWorkspaceId }),

  setAgentsForWorkspace: (workspaceId, agents) =>
    set((state) => ({
      agentsByWorkspace: {
        ...state.agentsByWorkspace,
        [workspaceId]: agents,
      },
    })),

  setWidgetForAgent: (agentId, widget) =>
    set((state) => ({
      widgetsByAgent: {
        ...state.widgetsByAgent,
        [agentId]: widget,
      },
    })),

  setTrialStatus: (trialStatus) => set({ trialStatus, trialStatusFetchedAt: Date.now() }),

  reset: () =>
    set({
      user: null,
      isAuthLoading: false,
      workspaces: [],
      activeWorkspaceId: null,
      agentsByWorkspace: {},
      widgetsByAgent: {},
      trialStatus: null,
      trialStatusFetchedAt: 0,
    }),
}));

